import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

type Props = {
  /** Show the bar (typically `formState.isDirty`). */
  visible: boolean
  isSaving?: boolean
  /** Disable Save (offline, invalid…) while still offering Discard. */
  saveDisabled?: boolean
  saveTitle?: string
  onDiscard: () => void
  /** Omit when the bar sits inside a <form>; Save then submits it. */
  onSave?: () => void
}

/**
 * One way of saving (design 15): editing a text field brings up a sticky bar
 * at the bottom of the page instead of per-field save buttons. Place it as
 * the last child of the page (inside the form when there is one).
 */
export function SettingsSaveBar({
  visible,
  isSaving = false,
  saveDisabled = false,
  saveTitle,
  onDiscard,
  onSave,
}: Props) {
  const { t } = useTranslation('settings')
  if (!visible) return null

  return (
    <div className="pointer-events-none sticky bottom-5 z-30 mt-auto pt-6">
      <div
        role="region"
        aria-label={t('saveBar.unsaved')}
        className={cn(
          // Ink bar on the light canvas; in dark mode a raised surface instead of
          // an inverted (bright) slab.
          'pointer-events-auto flex items-center gap-3 rounded-xl bg-foreground py-2 pr-2 pl-4 text-background shadow-xl',
          'dark:bg-popover dark:text-popover-foreground dark:ring-1 dark:ring-border',
          'animate-in fade-in slide-in-from-bottom-2 duration-200',
        )}
      >
        <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-brand" />
        <p className="min-w-0 flex-1 truncate text-sm font-medium" aria-live="polite">
          {t('saveBar.unsaved')}
        </p>
        <button
          type="button"
          onClick={onDiscard}
          disabled={isSaving}
          className="h-8 rounded-md px-3 text-sm text-background/70 transition-colors hover:text-background disabled:opacity-50 dark:text-muted-foreground dark:hover:text-foreground"
        >
          {t('saveBar.discard')}
        </button>
        <button
          type={onSave ? 'button' : 'submit'}
          onClick={onSave}
          disabled={isSaving || saveDisabled}
          title={saveTitle}
          className="inline-flex h-8 items-center gap-1.5 rounded-md bg-background px-4 text-sm font-semibold text-foreground transition-opacity hover:opacity-90 disabled:opacity-50 dark:bg-foreground dark:text-background"
        >
          {isSaving && <Loader2 className="size-3.5 animate-spin" />}
          {isSaving ? t('saveBar.saving') : t('saveBar.save')}
        </button>
      </div>
    </div>
  )
}
